<template>
<div class="mcp-doc">

    <!-- Quick facts -->
    <div class="mcp-facts">
        <div class="fact">
            <span class="fact__label">URL do conector</span>
            <code class="fact__value">https://mcp.interfy.io/mcp</code>
        </div>
        <div class="fact">
            <span class="fact__label">Autenticação</span>
            <span class="fact__value">OAuth 2.1 — entre com a sua conta</span>
        </div>
        <div class="fact">
            <span class="fact__label">Compatível com</span>
            <span class="fact__value">Claude, conectores do ChatGPT e outros clientes MCP</span>
        </div>
    </div>

    <!-- Capabilities -->
    <section class="block">
        <h2>O que você pode fazer</h2>
        <p class="lead">O conector expõe uma parte selecionada da plataforma em três áreas:</p>

        <div class="cap-grid">
            <div class="cap-card">
                <h3><span class="tag tag--bpm">BPM</span> Gestão de Processos de Negócio</h3>
                <ul>
                    <li>Listar suas tarefas abertas, disponíveis e concluídas</li>
                    <li>Assumir uma tarefa, preencher o formulário e concluí-la para avançar o fluxo</li>
                    <li>Iniciar um novo caso a partir de um modelo de processo</li>
                    <li>Reatribuir, cancelar ou reabrir tarefas (para supervisores)</li>
                    <li>Ler notas do caso, o andamento e métricas do processo</li>
                </ul>
            </div>
            <div class="cap-card">
                <h3><span class="tag tag--ecm">ECM</span> Gestão de Conteúdo Empresarial</h3>
                <ul>
                    <li>Pesquisar documentos e navegar por pastas</li>
                    <li>Ler detalhes, versões e comentários de um documento</li>
                    <li>Criar pastas e documentos, mover documentos, adicionar comentários</li>
                    <li>Compartilhar um documento com um usuário (expiração opcional) e revogar</li>
                </ul>
            </div>
            <div class="cap-card">
                <h3><span class="tag tag--dss">DSS</span> Serviço de Assinatura Digital</h3>
                <ul>
                    <li>Listar e inspecionar solicitações de assinatura, incl. status por signatário</li>
                    <li>Criar uma nova solicitação de assinatura para signatários reais</li>
                    <li>Cancelar uma solicitação de assinatura pendente</li>
                </ul>
            </div>
        </div>

        <p class="callout">
            <strong>Dica:</strong> peça ao assistente para executar “quem sou eu” primeiro — isso
            confirma a sua identidade e o workspace conectado antes de qualquer outra ação.
        </p>
    </section>

    <!-- Getting connected -->
    <section class="block">
        <h2>Como conectar</h2>

        <h3 class="sub">Claude.ai / ChatGPT <span class="pill">recomendado</span></h3>
        <ol class="steps">
            <li>Adicione um conector personalizado apontando para <code>https://mcp.interfy.io/mcp</code>.</li>
            <li>Quando solicitado, você será redirecionado para entrar (e escolher o workspace, caso pertença a mais de um).</li>
            <li>Aprove as permissões que deseja conceder ao assistente (veja Permissões abaixo).</li>
            <li>Pronto — o assistente já pode agir no seu workspace.</li>
        </ol>

        <h3 class="sub">Claude Code</h3>
        <p>Execute:</p>
        <pre><code>claude mcp add --transport http interfy https://mcp.interfy.io/mcp</code></pre>
        <p class="muted">O Claude Code conduz você pelo mesmo fluxo de login e consentimento. O seu
        workspace é determinado automaticamente a partir do login — você não precisa informá-lo manualmente.</p>
    </section>

    <!-- Permissions -->
    <section class="block">
        <h2>Permissões</h2>
        <p class="lead">
            Ao conectar, você concede um ou mais escopos. Solicite apenas leitura ou adicione
            acesso de escrita por área — e revogue o acesso a qualquer momento.
        </p>
        <div class="table-wrap">
            <table class="mcp-table">
                <thead>
                    <tr><th>Permissão</th><th>O que permite</th></tr>
                </thead>
                <tbody>
                    <tr><td><code>mcp:read</code></td><td>Ler tarefas, casos, documentos, pastas, assinaturas e usuários do workspace</td></tr>
                    <tr><td><code>mcp:bpm:write</code></td><td>Assumir / concluir / iniciar / reatribuir tarefas e casos, adicionar notas</td></tr>
                    <tr><td><code>mcp:ecm:write</code></td><td>Criar / mover / compartilhar documentos e pastas, adicionar comentários</td></tr>
                    <tr><td><code>mcp:dss:write</code></td><td>Criar e cancelar solicitações de assinatura</td></tr>
                </tbody>
            </table>
        </div>
        <p class="muted">
            As ferramentas de leitura e de escrita são separadas e claramente identificadas, para
            que o assistente saiba quais ações são seguras e quais alteram dados antes de agir.
            Algumas ações têm efeitos reais — por exemplo, criar uma solicitação de assinatura envia
            e-mails a signatários reais — e o assistente sinaliza quando uma ação não é facilmente reversível.
        </p>
    </section>

    <!-- Security -->
    <section class="block">
        <h2>Segurança e privacidade</h2>
        <ul class="checks">
            <li>Login com OAuth 2.1 (PKCE) — nenhuma senha de longa duração ou chave de API é compartilhada com o cliente de IA.</li>
            <li>Passagem por design: o conector nunca armazena suas credenciais ou conteúdo. O acesso é usado para responder a cada requisição e depois descartado — sem banco de dados ou armazenamento de sessão no conector.</li>
            <li>Seu workspace permanece isolado — toda requisição roda estritamente dentro do seu próprio workspace autenticado.</li>
            <li>Nenhum dado de conversa é coletado. O conector só vê a requisição específica feita pelo assistente.</li>
            <li>Revogue quando quiser desconectando o conector ou revogando o token — o acesso é interrompido imediatamente.</li>
        </ul>
        <p class="muted">Consulte a <a href="https://interfy.ai/legal/privacy">Política de Privacidade</a> para todos os detalhes.</p>
    </section>

    <!-- Support -->
    <section class="block block--last">
        <h2>Suporte</h2>
        <p>Precisa de ajuda para conectar ou tem alguma dúvida sobre o conector?</p>
        <div class="support-links">
            <a href="/support" class="support-links__btn">Falar com o suporte</a>
            <a href="https://interfy.ai/legal/privacy" class="support-links__link">Política de privacidade</a>
        </div>
    </section>

</div>
</template>

<script>
export default {
  props: {
    company: {
      type: Object,
      default () {
        return { company_name: 'Interfy', company_fullname: 'Interfy' }
      }
    }
  }
}
</script>

<style scoped lang="scss">
$blue: #214cdb;
$ink: #1f2937;
$muted: #5b6472;
$line: #e6e9ef;

.mcp-doc {
  color: $ink;
  font-size: 16px;
  line-height: 1.65;

  code {
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: .9em;
    color: $blue;
    background: #eef2ff;
    padding: 2px 7px;
    border-radius: 5px;
  }

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: $ink;
    margin: 0 0 6px;
  }

  .block { padding: 40px 0; border-top: 1px solid $line; }
  .block:first-of-type { border-top: 0; }
  .block--last { padding-bottom: 8px; }

  .lead { color: $muted; margin-bottom: 24px; }
  .muted { color: $muted; font-size: 15px; }

  /* Quick facts */
  .mcp-facts {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 8px;

    .fact {
      background: #f7f9fc;
      border: 1px solid $line;
      border-radius: 12px;
      padding: 18px 20px;

      &__label {
        display: block;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: .09em;
        color: #8a93a3;
        margin-bottom: 8px;
      }
      &__value {
        font-size: 15px;
        color: $ink;
        background: transparent;
        padding: 0;
        word-break: break-word;
      }
      code.fact__value { color: $blue; }
    }
  }

  /* Capabilities cards */
  .cap-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 24px;
  }
  .cap-card {
    border: 1px solid $line;
    border-radius: 14px;
    padding: 22px 22px 8px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, .04);
    transition: box-shadow .2s ease, transform .2s ease;

    &:hover { box-shadow: 0 8px 24px rgba(16, 24, 40, .08); transform: translateY(-2px); }

    h3 {
      font-size: 15px;
      font-weight: 700;
      line-height: 1.35;
      margin-bottom: 14px;
    }
    ul { padding-left: 0; list-style: none; margin: 0; }
    li {
      position: relative;
      padding-left: 20px;
      margin-bottom: 10px;
      font-size: 14.5px;
      color: #414b5a;
    }
    li::before {
      content: "";
      position: absolute;
      left: 2px; top: 9px;
      width: 6px; height: 6px;
      border-radius: 50%;
      background: $blue;
    }
  }

  .tag {
    display: inline-block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .04em;
    color: #fff;
    padding: 2px 8px;
    border-radius: 6px;
    margin-right: 8px;
    vertical-align: middle;
  }
  .tag--bpm { background: #2f5bff; }
  .tag--ecm { background: #0ea5a3; }
  .tag--dss { background: #7c3aed; }

  /* Connect */
  h3.sub { font-size: 18px; font-weight: 700; margin: 26px 0 12px; }
  .pill {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
    color: #0a7d3c; background: #e6f6ec; padding: 3px 9px; border-radius: 999px; margin-left: 6px;
    vertical-align: middle;
  }
  ol.steps { padding-left: 20px; }
  ol.steps li { margin-bottom: 8px; }

  pre {
    background: #0f172a;
    color: #e5e7eb;
    padding: 16px 18px;
    border-radius: 10px;
    overflow-x: auto;
    margin: 6px 0 14px;
    code { color: #e5e7eb; background: transparent; padding: 0; font-size: 14px; }
  }

  .callout {
    background: #eef4ff;
    border: 1px solid #d7e3ff;
    border-left: 4px solid $blue;
    color: #23324d;
    padding: 14px 18px;
    border-radius: 10px;
    font-size: 15px;
    margin: 4px 0 0;
  }

  /* Table */
  .table-wrap { overflow-x: auto; border: 1px solid $line; border-radius: 12px; margin-bottom: 16px; }
  .mcp-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 15px;
    th, td { text-align: left; padding: 13px 18px; vertical-align: top; }
    thead th { background: #f7f9fc; font-weight: 700; color: $ink; border-bottom: 1px solid $line; }
    tbody tr + tr td { border-top: 1px solid $line; }
    td code { white-space: nowrap; }
  }

  /* Security checklist */
  ul.checks { list-style: none; padding-left: 0; margin: 8px 0 12px; }
  ul.checks li {
    position: relative;
    padding-left: 30px;
    margin-bottom: 12px;
    color: #414b5a;
  }
  ul.checks li::before {
    content: "✓";
    position: absolute; left: 0; top: 0;
    width: 20px; height: 20px;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800;
    color: #0a7d3c; background: #e6f6ec; border-radius: 50%;
  }

  /* Support */
  .support-links { display: flex; align-items: center; gap: 18px; margin-top: 16px; flex-wrap: wrap; }
  .support-links__btn {
    display: inline-block;
    background: $blue; color: #fff;
    padding: 12px 26px; border-radius: 999px;
    font-weight: 600; text-decoration: none;
    transition: background .2s ease;
    &:hover { background: #1a3cae; color: #fff; }
  }
  .support-links__link { color: $blue; font-weight: 600; text-decoration: none; &:hover { text-decoration: underline; } }

  a { color: $blue; }
}

@media only screen and (max-width: 991px) {
  .mcp-doc {
    .mcp-facts { grid-template-columns: 1fr; }
    .cap-grid { grid-template-columns: 1fr; }
    h2 { font-size: 24px; }
  }
}
</style>
